// Gmail v1: the signed-in user's threads and labels.
// https://developers.google.com/workspace/gmail/api/reference/rest

import type { GoogleApi } from "./client.js"

const BASE = "https://gmail.googleapis.com/gmail/v1/users/me"

export interface MessageHeader {
  name?: string
  value?: string
}

export interface MessagePart {
  mimeType?: string
  /** The file's name, on a part that is an attachment. */
  filename?: string
  headers?: MessageHeader[]
  /** `data` is base64url. */
  body?: { size?: number; data?: string; attachmentId?: string }
  parts?: MessagePart[]
}

export interface MessageResource {
  id?: string
  threadId?: string
  labelIds?: string[]
  snippet?: string
  /** Epoch milliseconds, as a string. */
  internalDate?: string
  payload?: MessagePart
}

export interface ThreadResource {
  id?: string
  historyId?: string
  snippet?: string
  messages?: MessageResource[]
}

export interface LabelResource {
  id?: string
  name?: string
  type?: "system" | "user"
}

export interface ListThreadsParams {
  /** Gmail search syntax. */
  q?: string
  /** Only threads carrying every one of these labels. */
  labelIds?: string[]
  maxResults?: number
  /** The `nextPageToken` of the page before. */
  pageToken?: string
}

export function createGmail(api: GoogleApi) {
  return {
    threads: {
      /** One page of thread ids and snippets (`get` a thread for its messages),
       *  and the token for the page after it, absent on the last. */
      list: async (params: ListThreadsParams = {}) => {
        const { threads, nextPageToken } = await api.get<{
          threads?: ThreadResource[]
          nextPageToken?: string
        }>(`${BASE}/threads`, { ...params })
        return { threads: threads ?? [], nextPageToken: nextPageToken ?? null }
      },

      get: (id: string) =>
        api.get<ThreadResource>(`${BASE}/threads/${encodeURIComponent(id)}`, { format: "full" }),

      modify: (id: string, labels: { addLabelIds: string[]; removeLabelIds: string[] }) =>
        api.post<ThreadResource>(`${BASE}/threads/${encodeURIComponent(id)}/modify`, labels),

      trash: (id: string) =>
        api.post<ThreadResource>(`${BASE}/threads/${encodeURIComponent(id)}/trash`),

      untrash: (id: string) =>
        api.post<ThreadResource>(`${BASE}/threads/${encodeURIComponent(id)}/untrash`),
    },

    labels: {
      list: async () => {
        const { labels } = await api.get<{ labels?: LabelResource[] }>(`${BASE}/labels`)
        return labels ?? []
      },
    },
  }
}
